import React, { useEffect, useState } from 'react';
import {
  CompletionOverrideReview,
  decideCompletionOverride,
  fetchCompletionOverrideByToken,
} from '../../data/api';

interface Props {
  token: string;
  onDone: () => void;
}

/** Where the approval email's link lands: one request, one decision.
 *
 * Behind login on purpose -- the person deciding is an AVCS superuser and
 * the decision is written against their account. A staff user who follows
 * the link can read the request but is told they cannot decide. */
export const CompletionOverrideReviewScreen: React.FC<Props> = ({ token, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [review, setReview] = useState<CompletionOverrideReview | null>(null);
  const [note, setNote] = useState('');
  const [deciding, setDeciding] = useState<'APPROVED' | 'DECLINED' | null>(null);
  const [decideError, setDecideError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCompletionOverrideByToken(token);
        if (!cancelled) setReview(data);
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'This review link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const decide = async (decision: 'APPROVED' | 'DECLINED') => {
    if (!review || deciding) return;
    setDeciding(decision);
    setDecideError('');
    try {
      const updated = await decideCompletionOverride(review.id, decision, note.trim());
      setReview({ ...review, ...updated });
    } catch (err: any) {
      setDecideError(err?.message || 'The decision could not be saved.');
    } finally {
      setDeciding(null);
    }
  };

  const fmtWhen = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  if (loading) {
    return (
      <main className="card form-card" role="main">
        <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
        <span className="tag">Deal close-out override</span>
        <p className="desc">Loading the request…</p>
      </main>
    );
  }

  if (loadError || !review) {
    return (
      <main className="card form-card" role="main" aria-labelledby="overrideTitle">
        <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
        <span className="tag">Deal close-out override</span>
        <h1 id="overrideTitle">This link no longer works</h1>
        <div className="alert error" role="alert">{loadError || 'This review link is invalid or has expired.'}</div>
        <p className="desc">Review links work for 14 days. Ask the requester to send the override request again.</p>
        <div className="actions">
          <button className="btn" type="button" onClick={onDone}>Go to AVCS</button>
        </div>
      </main>
    );
  }

  const deal = review.dealSummary;
  const decided = review.status !== 'PENDING';
  const approved = review.status === 'APPROVED';

  return (
    <main className="card form-card" role="main" aria-labelledby="overrideTitle">
      <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
      <span className="tag">Deal close-out override</span>
      <h1 id="overrideTitle">
        Close deal {deal.dealNo} without {review.waivedRequirements.length} requirement{review.waivedRequirements.length === 1 ? '' : 's'}?
      </h1>
      <p className="desc">
        {deal.primaryProducer || '—'}{deal.valueChain ? ` · ${deal.valueChain}` : ''}
        {deal.totalAnimals != null ? ` · ${deal.totalAnimals} animals` : ''}{deal.dealDate ? ` · deal dated ${deal.dealDate}` : ''}
        {deal.dealReferenceNo ? ` · ref ${deal.dealReferenceNo}` : ''}
      </p>

      <div className="grid gap-3 text-left">
        <div className="rounded-xl border border-[#1e6bb824] bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-avcs-muted">Requested by</div>
          <div className="mt-0.5 text-sm text-avcs-text">{review.requestedByName || 'An administrator'} · {fmtWhen(review.requestedAt)}</div>
          <div className="mt-3 text-xs uppercase tracking-wide text-avcs-muted">Reason</div>
          <p className="mt-0.5 text-sm text-avcs-text">“{review.reason}”</p>
        </div>

        <div className="rounded-xl border border-[#ecd1b9] bg-[#fff7ef] p-4">
          <div className="text-xs uppercase tracking-wide text-[#7b4a18]">Approving waives exactly these</div>
          <ul className="mt-2 grid gap-1.5">
            {review.waivedRequirements.map((r) => (
              <li key={`${r.party}-${r.code}`} className="text-sm text-avcs-text">
                <span className="font-semibold">{r.label}</span>
                {r.party && <span className="text-avcs-muted"> ({r.party.toLowerCase()})</span>}
                {r.reason && <div className="text-xs text-avcs-muted">{r.reason}</div>}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-avcs-muted">
            Anything the deal comes to need after this approval still has to be captured, or requested separately.
          </p>
        </div>

        {decided ? (
          <div className={`alert ${approved ? 'success' : 'error'}`} role="status">
            {approved ? 'Approved' : 'Declined'} by {review.decidedByName || 'a superuser'} on {fmtWhen(review.decidedAt)}.
            {review.decisionNote ? ` Note: “${review.decisionNote}”` : ''}
            {approved && !review.appliedAt ? ' The deal can now be closed from its View deal screen.' : ''}
            {review.appliedAt ? ` The deal was closed on ${fmtWhen(review.appliedAt)}.` : ''}
          </div>
        ) : review.canDecide ? (
          <form className="grid gap-3" onSubmit={(event) => event.preventDefault()} aria-label="Decide on the override">
            <label className="grid gap-1 text-sm font-medium text-avcs-text">
              Note for the requester (optional)
              <textarea
                className="min-h-20 rounded-lg border border-[#1e6bb824] bg-white px-3 py-2 text-sm font-normal text-avcs-text"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="e.g. Agreed with the purchaser by phone on 4 Sep."
              />
            </label>
            {decideError && <div className="alert error" role="alert">{decideError}</div>}
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className="btn" onClick={() => decide('DECLINED')} disabled={deciding !== null} aria-busy={deciding === 'DECLINED'}>
                {deciding === 'DECLINED' ? 'Declining…' : 'Decline'}
              </button>
              <button type="button" className="btn primary" onClick={() => decide('APPROVED')} disabled={deciding !== null} aria-busy={deciding === 'APPROVED'}>
                {deciding === 'APPROVED' ? 'Approving…' : 'Approve and waive'}
              </button>
            </div>
          </form>
        ) : (
          <div className="alert error" role="status">
            Only a superuser can approve or decline this request. It is still awaiting a decision.
          </div>
        )}
      </div>

      <div className="actions">
        <button className="btn" type="button" onClick={onDone}>Go to AVCS</button>
      </div>
    </main>
  );
};
